import {
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import type { ReactNode } from "react";
import { semanticColors } from "../../theme/tokens";

export type TagVariant = "soft" | "outline";
export type TagTone =
  | "neutral"
  // Where she is, not a status: the roadmap's "Current" and "You're here".
  | "accent"
  | "danger"
  | "success"
  | "warning"
  // Deprecated names for the status tones (#3910): `gold` is `warning`,
  // `coral` is `danger` and `green` is `success`.
  | "gold"
  | "coral"
  | "green"
  | "orange"
  | "blue"
  | "purple"
  | "chatNeutral"
  | "sourceBlue"
  | "sourcePurple"
  | "sourceGreen"
  | "onDark";
export type TagShape = "rounded" | "pill";
export type TagSize = "default" | "compact";

type TagColorSet = {
  text: string;
  bg: string;
  /** Outline border; the light tones share `OUTLINE_BORDER`. */
  border?: string;
};

const ROUNDED_RADIUS = 6;
const OUTLINE_RADIUS = 8;
const OUTLINE_BORDER = "rgba(0,0,0,0.06)";

/** The role tones (#3910): the role's word colour on its soft tint. */
const ROLE_TONE_COLORS = {
  accent: {
    text: semanticColors.accent.default,
    bg: semanticColors.accent.tint,
  },
  danger: {
    text: semanticColors.status.danger,
    bg: semanticColors.status.dangerTint,
  },
  success: {
    text: semanticColors.status.success,
    bg: semanticColors.status.successTint,
  },
  warning: {
    text: semanticColors.status.warningText,
    bg: semanticColors.status.warningTint,
  },
} as const satisfies Record<string, TagColorSet>;

const SOFT_TONE_COLORS: Record<TagTone, TagColorSet> = {
  neutral: { text: "#6B5548", bg: "rgba(139, 111, 94, 0.1)" },
  ...ROLE_TONE_COLORS,
  gold: ROLE_TONE_COLORS.warning,
  coral: ROLE_TONE_COLORS.danger,
  green: ROLE_TONE_COLORS.success,
  orange: { text: "#9A4D19", bg: "rgba(232, 146, 44, 0.18)" },
  blue: { text: "#4E638F", bg: "rgba(123, 142, 181, 0.18)" },
  purple: { text: "#72528F", bg: "rgba(151, 116, 181, 0.18)" },
  chatNeutral: { text: "#707070", bg: "rgba(112, 112, 112, 0.08)" },
  sourceBlue: { text: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  sourcePurple: { text: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  sourceGreen: { text: "#4E7F61", bg: "rgba(130, 212, 168, 0.18)" },
  // For a dark card or texture: a white wash with light text.
  onDark: {
    text: semanticColors.onDark.text.secondary,
    bg: semanticColors.onDark.fill.strong,
  },
};

const OUTLINE_TONE_COLORS: Record<TagTone, TagColorSet> = {
  // Dark enough to read as a label on a light card, not as hint text.
  neutral: { text: "#6B5548", bg: "transparent" },
  accent: { text: ROLE_TONE_COLORS.accent.text, bg: "transparent" },
  danger: { text: ROLE_TONE_COLORS.danger.text, bg: "transparent" },
  success: { text: ROLE_TONE_COLORS.success.text, bg: "transparent" },
  warning: { text: ROLE_TONE_COLORS.warning.text, bg: "transparent" },
  gold: { text: ROLE_TONE_COLORS.warning.text, bg: "transparent" },
  coral: { text: ROLE_TONE_COLORS.danger.text, bg: "transparent" },
  green: { text: ROLE_TONE_COLORS.success.text, bg: "transparent" },
  orange: { text: "#9A4D19", bg: "transparent" },
  blue: { text: "#4E638F", bg: "transparent" },
  purple: { text: "#72528F", bg: "transparent" },
  chatNeutral: { text: "#707070", bg: "transparent" },
  sourceBlue: { text: "#3B82F6", bg: "transparent" },
  sourcePurple: { text: "#8B5CF6", bg: "transparent" },
  sourceGreen: { text: "#4E7F61", bg: "transparent" },
  // For a dark card or texture: a faint white fill inside a white hairline.
  onDark: {
    text: semanticColors.onDark.text.secondary,
    bg: semanticColors.onDark.fill.subtle,
    border: semanticColors.onDark.fill.subtle,
  },
};

const VARIANT_TONE_COLORS: Record<TagVariant, Record<TagTone, TagColorSet>> = {
  soft: SOFT_TONE_COLORS,
  outline: OUTLINE_TONE_COLORS,
};

type TagMetrics = {
  gap: number;
  minHeight?: number;
  paddingHorizontal: number;
  paddingVertical: number;
  iconSize: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
};

const SIZE_METRICS: Record<TagSize, Record<TagVariant, TagMetrics>> = {
  default: {
    soft: {
      gap: 4,
      minHeight: 18,
      paddingHorizontal: 8,
      paddingVertical: 3,
      iconSize: 11,
      fontSize: 11,
      lineHeight: 14,
      letterSpacing: 0,
    },
    outline: {
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      iconSize: 10,
      fontSize: 11,
      lineHeight: 14,
      letterSpacing: 0.2,
    },
  },
  // For dense cards, such as two-column grids: 9px labels in an 18px tag, the
  // density the Find Care clinic cards had before their tags were shared.
  compact: {
    soft: {
      gap: 3,
      paddingHorizontal: 6,
      paddingVertical: 3,
      iconSize: 9,
      fontSize: 9,
      lineHeight: 12,
      letterSpacing: 0,
    },
    outline: {
      gap: 3,
      paddingHorizontal: 7,
      paddingVertical: 2,
      iconSize: 9,
      fontSize: 9,
      lineHeight: 12,
      letterSpacing: 0,
    },
  },
};

export type TagProps = {
  label: string;
  variant?: TagVariant;
  tone?: TagTone;
  icon?: LucideIcon;
  leading?: ReactNode;
  trailing?: ReactNode;
  shape?: TagShape;
  /** `compact` is for dense cards where the default size would wrap or crowd. */
  size?: TagSize;
  iconOnly?: boolean;
  accessibilityLabel?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

export function Tag({
  label,
  variant = "soft",
  tone = "neutral",
  icon: Icon,
  leading,
  trailing,
  shape = "rounded",
  size = "default",
  iconOnly = false,
  accessibilityLabel,
  style,
  textStyle,
}: TagProps) {
  const toneColors = VARIANT_TONE_COLORS[variant][tone];
  const isOutline = variant === "outline";
  const isCompact = size === "compact";
  const metrics = SIZE_METRICS[size][variant];
  const radius =
    shape === "pill" ? 999 : isOutline ? OUTLINE_RADIUS : ROUNDED_RADIUS;

  return (
    <View
      accessibilityLabel={accessibilityLabel ?? label}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: metrics.gap,
          minHeight: metrics.minHeight,
          paddingHorizontal: iconOnly
            ? isCompact
              ? 3
              : 4
            : metrics.paddingHorizontal,
          paddingVertical: iconOnly ? 2 : metrics.paddingVertical,
          borderRadius: radius,
          borderWidth: isOutline ? 1 : 0,
          borderColor: isOutline
            ? (toneColors.border ?? OUTLINE_BORDER)
            : "transparent",
          backgroundColor: toneColors.bg,
        },
        style,
      ]}
    >
      {leading ??
        (Icon ? (
          <Icon
            size={iconOnly ? (isCompact ? 10 : 12) : metrics.iconSize}
            color={toneColors.text}
            strokeWidth={isOutline ? 1.5 : 1.8}
          />
        ) : null)}
      {iconOnly ? null : (
        <Text
          className="font-dm-sans"
          style={[
            {
              fontSize: metrics.fontSize,
              lineHeight: metrics.lineHeight,
              fontWeight: isOutline ? "500" : "600",
              color: toneColors.text,
              letterSpacing: metrics.letterSpacing,
            },
            textStyle,
          ]}
        >
          {label}
        </Text>
      )}
      {trailing}
    </View>
  );
}

export default Tag;
